import { IconAlertTriangle, IconPlayerPause } from "@tabler/icons-react";
import { Outlet } from "react-router-dom";

import { useSession } from "../../auth/SessionProvider";
import { DEV_MODE_USERNAME } from "../../config";
import { Sidebar } from "./Sidebar";

/** Banner shown whenever the server accepts an unauthenticated header. */
function DevModeBanner(): JSX.Element {
  return (
    <p className="banner banner--dev" role="status">
      <IconAlertTriangle size={14} aria-hidden="true" />
      Development mode, no authentication — the server trusts whatever name this
      page sends, and this page sends “{DEV_MODE_USERNAME}”. Never run a shared
      cluster like this.
    </p>
  );
}

/**
 * The emergency stop, said once, on every screen.
 *
 * Shown from `me.console_paused`, which is the server's own answer — a
 * researcher who does not know the console is paused reads every refusal as a
 * bug in the console.
 */
function PausedBanner(): JSX.Element {
  return (
    <p className="banner banner--paused" role="status">
      <IconPlayerPause size={14} aria-hidden="true" />
      The console is paused: no new workspace and no new machine can start right
      now. Workspaces already running are untouched.
    </p>
  );
}

/**
 * The console frame: a named sidebar on the left, the screen filling the rest.
 *
 * Full width on purpose — the fleet is the content, and it earns the room.
 *
 * Returns:
 *     The application shell.
 */
export function AppShell(): JSX.Element {
  const { authConfig, me } = useSession();
  return (
    <div className="shell">
      <Sidebar />
      <div className="shell__main">
        {authConfig.mode === "dev" ? <DevModeBanner /> : null}
        {me.console_paused ? <PausedBanner /> : null}
        <main className="shell__content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
